/**
 * Date utility functions that handle dates without timezone conversion
 * This ensures dates like "1973-08-22" display the same regardless of user's timezone
 */

/**
 * Parse a date string (YYYY-MM-DD) and return date components without timezone conversion
 * @param {string} dateValue - Date string in YYYY-MM-DD format
 * @returns {Object|null} - Object with year, month (1-12), day (1-31) or null if invalid
 */
export const parseDateComponents = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // If it's a YYYY-MM-DD string, parse it directly
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
      const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return {
          year: parseInt(match[1], 10),
          month: parseInt(match[2], 10), // 1-12
          day: parseInt(match[3], 10), // 1-31
        };
      }
    }
    
    // Fallback: try to parse as Date and use UTC methods
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;
    
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1, // 1-12
      day: date.getUTCDate(), // 1-31
    };
  } catch (e) {
    return null;
  }
};

/**
 * Format date for display (e.g., "August 22, 1973")
 * @param {string} dateValue - Date string
 * @returns {string} - Formatted date string
 */
export const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return '';
  
  const components = parseDateComponents(dateValue);
  if (!components) return '';
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthName = monthNames[components.month - 1];
  return `${monthName} ${components.day}, ${components.year}`;
};

/**
 * Format date in short format (e.g., "Aug 22, 1973")
 * @param {string} dateValue - Date string
 * @returns {string} - Formatted date string
 */
export const formatDateShort = (dateValue) => {
  if (!dateValue) return '';
  
  const components = parseDateComponents(dateValue);
  if (!components) return '';
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = monthNames[components.month - 1];
  return `${monthName} ${components.day}, ${components.year}`;
};

/**
 * Format date for HTML date input (yyyy-MM-dd)
 * @param {string} dateValue - Date string
 * @returns {string} - Date in yyyy-MM-dd format
 */
export const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    // If it's already in yyyy-MM-dd format, return as is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    const components = parseDateComponents(dateValue);
    if (!components) return '';
    
    const month = String(components.month).padStart(2, '0');
    const day = String(components.day).padStart(2, '0');
    return `${components.year}-${month}-${day}`;
  } catch (e) {
    return '';
  }
};

/**
 * Get month name (short) from date
 * @param {string} dateValue - Date string
 * @returns {string} - Month name like "Aug"
 */
export const getMonthNameShort = (dateValue) => {
  const components = parseDateComponents(dateValue);
  if (!components) return '';
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return monthNames[components.month - 1];
};

/**
 * Get month name (full) from date
 * @param {string} dateValue - Date string
 * @returns {string} - Month name like "August"
 */
export const getMonthNameFull = (dateValue) => {
  const components = parseDateComponents(dateValue);
  if (!components) return '';
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return monthNames[components.month - 1];
};

/**
 * Get year from date
 * @param {string} dateValue - Date string
 * @returns {number|null} - Year or null if invalid
 */
export const getYear = (dateValue) => {
  const components = parseDateComponents(dateValue);
  return components ? components.year : null;
};

/**
 * Get month from date (1-12)
 * @param {string} dateValue - Date string
 * @returns {number|null} - Month (1-12) or null if invalid
 */
export const getMonth = (dateValue) => {
  const components = parseDateComponents(dateValue);
  return components ? components.month : null;
};

/**
 * Get day from date (1-31)
 * @param {string} dateValue - Date string
 * @returns {number|null} - Day (1-31) or null if invalid
 */
export const getDay = (dateValue) => {
  const components = parseDateComponents(dateValue);
  return components ? components.day : null;
};


